import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, DashedButton, Icon, Notification, RadioGroup } from "@telia/teddy";
import _values from "lodash/values";
import { useSelector, useDispatch } from "react-redux";
import { getHeading } from "./utils";
import { isZeroPlaySubscription, isNonMhsSubscription } from "../../../../../../shared/subscriptionCodeUtils";
import { CART_ITEM_TYPE, FORM_ID } from "../../../../../../constants";
import { MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS, NEW_ACCOUNT_VALUE } from "../../../../../../constants/app";
import { RootState } from "../../../../../../models";
import { removeItemByIdFromCart, setActiveSubscription } from "../../../../checkout/state/actions/cartActions";
import * as AT from "../../../../checkout/state/actionTypes";
import { getActiveSubscription, getAllItemsOfType } from "../../../../checkout/state/selectors/cartSelectors";
import { getSubscriptions } from "../../../../checkout/state/selectors/subscriptionsSelectors";
import * as LOADING_SELECTORS from "../../../../loading/state/selectors";
import { setValue as setPersonalDetailsValue } from "../../../state/actions";
import { prefillCustomerForm } from "../../../state/actions/customerActions";
import {
    clearExistingSubscriptionForChange,
    setExistingSubscriptionForChange,
    setIsAddingSubscription,
    setSubscriptionStep,
} from "../../../state/actions/subscriptionStepsActions";
import {
    getCustomerExistingAccounts,
    getCustomerLegalOwnerIndividual,
    getCustomerSelectedAccountId,
    getExistingSubscriptionForChange,
} from "../../../state/selectors/customerSelectors";
import { isAddingSubscription as isAddingSubscriptionSelector } from "../../../state/selectors/subscriptionStepsSelectors";
import { isCountableSubscription } from "../../../utils";
import RetailerSubscriptionAccordion from "../../organisms/RetailerSubscriptionAccordion/RetailerSubscriptionAccordion";
import { AddSubscriptionSteps } from "../AddSubscriptionSteps";
import ExistingSubscriptionDiscardModal from "../ExistingSubscriptionDiscardModal/ExistingSubscriptionDiscardModal";
import { actions as RAPIDFORM_ACTIONS } from "../../../../rapidform";
import "./AddNewSubscriptions.less";

const AddNewSubscriptions = () => {
    const dispatch = useDispatch();
    const legalOwner = useSelector((state: RootState) => getCustomerLegalOwnerIndividual(state));
    const subscriptions = useSelector(getSubscriptions);
    const draftSubscriptions = useSelector((state: RootState) =>
        getAllItemsOfType(state, CART_ITEM_TYPE.SUBSCRIPTION_DRAFT),
    );
    const activeSubscription = useSelector((state: RootState) => getActiveSubscription(state));
    const existingSubscriptionForChange = useSelector(getExistingSubscriptionForChange);
    const numberOfAddedSubscriptions = _values(subscriptions).length;
    const isLoading = useSelector(
        (state: RootState) =>
            LOADING_SELECTORS.isTypeLoading(state, AT.CHECKOUT_ADD_SUBSCRIPTION_TO_CART) ||
            LOADING_SELECTORS.isTypeLoading(state, AT.CHECKOUT_CHECK_NEW_MEMBER_PHONE_NUMBER),
    );
    const isAddingSubscription = useSelector((state: RootState) => isAddingSubscriptionSelector(state));
    const existingAccounts = useSelector((state: RootState) => getCustomerExistingAccounts(state));
    const selectedAccountId = useSelector((state: RootState) => getCustomerSelectedAccountId(state));
    const isNewCustomer = existingAccounts.length === 0;
    const [discardModalSubscriptionId, setDiscardModalSubscriptionId] = useState<string | null>(null);
    const addButtonContainerRef = useRef<HTMLDivElement>(null);
    const hasFocusedAddButtonRef = useRef(false);

    const onSubscriptionSelected = (subscriptionId: string) => {
        const subscription = _values(subscriptions).find((sub) => sub.id === subscriptionId);
        if (!subscription) return;

        dispatch(setActiveSubscription(subscription.id));
        dispatch(setSubscriptionStep(subscription.id, 0));
        dispatch(setPersonalDetailsValue("isCatalogListingOk", subscription.isCatalogListingOk ?? true));
        dispatch(prefillCustomerForm(subscription.id, false, true));
    };

    const isAddNewSubscriptionBlocked = useMemo(() => {
        if (!selectedAccountId || selectedAccountId === NEW_ACCOUNT_VALUE) return false;
        const account = existingAccounts.find((acc) => acc.accountId === selectedAccountId);
        if (!account) return false;
        const existingCountableCount = account.subscriptions.filter(isCountableSubscription).length;
        if (existingCountableCount >= MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS) return false;
        const existingPhoneNumbers = new Set(account.subscriptions.map((sub) => sub.phoneNumber));
        const newCartAdditions = _values(subscriptions).filter((sub) => {
            if (isNonMhsSubscription(sub.code)) return false;
            if (sub.id === activeSubscription) return false;
            const phone = sub.phoneNumber?.selectedPhoneNumber;
            if (!phone) return false;
            if (existingPhoneNumbers.has(phone)) return false;
            return true;
        }).length;
        return existingCountableCount + newCartAdditions >= MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS;
    }, [selectedAccountId, existingAccounts, subscriptions, activeSubscription]);

    const isSelectedAccountNaturallyFull = useMemo(() => {
        if (!selectedAccountId || selectedAccountId === NEW_ACCOUNT_VALUE) return false;
        const account = existingAccounts.find((acc) => acc.accountId === selectedAccountId);
        if (!account) return false;
        return account.subscriptions.filter(isCountableSubscription).length >= MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS;
    }, [selectedAccountId, existingAccounts]);

    useEffect(() => {
        if (hasFocusedAddButtonRef.current) return;

        if (!activeSubscription && !isAddingSubscription && isNewCustomer) {
            const btn = addButtonContainerRef.current?.querySelector("button");
            if (btn) {
                btn.focus();
                hasFocusedAddButtonRef.current = true;
            }
        }
    }, [activeSubscription, isAddingSubscription, isNewCustomer]);

    if (!legalOwner?.firstName) {
        return (
            <Notification type="tile" variant="warning">
                <Notification.Icon name="error-filled" />
                <Notification.Text>
                    For å legge til et abonnement må du først identifisere kunden via kredittsjekk eller kundeoppslag.
                </Notification.Text>
            </Notification>
        );
    }

    return (
        <div className="add-new-subscriptions">
            {isAddingSubscription && (
                <div className="subscription-list">
                    <div className="subscriptionCard">
                        <div className="add-new-subscriptions__close-button">
                            <Button
                                variant="text"
                                onClick={() => {
                                    dispatch(setIsAddingSubscription(false));
                                    dispatch(clearExistingSubscriptionForChange());
                                }}
                                aria-label="Lukk"
                            >
                                <Icon name="close" aria-hidden="true" focusable="false" />
                            </Button>
                        </div>
                        <AddSubscriptionSteps id={activeSubscription} />
                    </div>
                </div>
            )}

            {numberOfAddedSubscriptions > 0 && (
                <div className="subscription-list">
                    <RadioGroup
                        value={activeSubscription || ""}
                        onValueChange={onSubscriptionSelected}
                        name="retailer-add-subscription-radio-group"
                    >
                        {_values(subscriptions)
                            .filter((sub) => {
                                const isExistingSubscription = !!sub.changeType;
                                return !isExistingSubscription || isAddingSubscription || activeSubscription;
                            })
                            .filter((sub) =>
                                isAddingSubscription || activeSubscription ? sub.id === activeSubscription : true,
                            )
                            .filter((sub) => !sub.existingSubscriptionUpdateScope || sub.id === activeSubscription)
                            .map((sub, index) => {
                                const isSelected = sub.id === activeSubscription;
                                const productUser = sub.productUser?.firstName || "";
                                const isZeroPlay = isZeroPlaySubscription(sub.code);
                                let title = "";
                                if (isZeroPlay) {
                                    title = [sub.productUser?.firstName, sub.productUser?.email]
                                        .filter(Boolean)
                                        .join(" - ");
                                } else if (productUser) {
                                    title = sub.name;
                                }

                                const isDraft = draftSubscriptions.some((draft) => draft.bundleId === sub.id);
                                const isExistingSubscription = !!sub.changeType;
                                const hasPhoneNumber = !!sub.phoneNumber?.selectedPhoneNumber;
                                const hasProductUser = !!(sub.productUser?.firstName && sub.productUser?.lastName);
                                const validationStatus: "success" | "warning" | undefined = !isSelected
                                    ? isDraft || !hasPhoneNumber || !hasProductUser
                                        ? "warning"
                                        : "success"
                                    : undefined;

                                return (
                                    <RetailerSubscriptionAccordion
                                        id={sub.id}
                                        isFirst={index === 0}
                                        isSelected={isSelected}
                                        isKeepExistingSubscription={!!sub.keepExistingSubscription}
                                        title={title}
                                        key={sub.id}
                                        changedFromName={
                                            sub.changeType ? existingSubscriptionForChange?.offeringName : undefined
                                        }
                                        changedToName={sub.changeType ? sub.name : undefined}
                                        onSelectSubscription={() => onSubscriptionSelected(sub.id)}
                                        onClose={
                                            isSelected
                                                ? () => {
                                                      if (isExistingSubscription && isDraft) {
                                                          setDiscardModalSubscriptionId(sub.id);
                                                      } else {
                                                          dispatch(setActiveSubscription());
                                                      }
                                                  }
                                                : undefined
                                        }
                                        price={""}
                                        name={getHeading(sub, draftSubscriptions, existingSubscriptionForChange)}
                                        validationStatus={validationStatus}
                                    >
                                        <AddSubscriptionSteps id={sub.id} />
                                    </RetailerSubscriptionAccordion>
                                );
                            })}
                    </RadioGroup>
                </div>
            )}
            {!activeSubscription && !isAddingSubscription && (
                <div ref={addButtonContainerRef}>
                    <DashedButton
                        disabled={isSelectedAccountNaturallyFull || isAddNewSubscriptionBlocked}
                        onClick={() => {
                            dispatch(setIsAddingSubscription(true));
                            dispatch(RAPIDFORM_ACTIONS.clearAll(FORM_ID.CUSTOMER_PRODUCT_USER));
                        }}
                        loading={isLoading}
                    >
                        Legg til nytt abonnement
                    </DashedButton>
                    {isSelectedAccountNaturallyFull && (
                        <Notification type="tile" variant="warning" mt="200">
                            <Notification.Icon name="error-filled" />
                            <Notification.Text>
                                Valgt fakturanummer er fullt. Velg et annet fakturanummer for å legge til nye
                                abonnement.
                            </Notification.Text>
                        </Notification>
                    )}
                    {isAddNewSubscriptionBlocked && (
                        <Notification type="tile" variant="warning" mt="200">
                            <Notification.Icon name="error-filled" />
                            <Notification.Text>
                                Faktura har nådd maksgrense for antall abonnement. Det er ikke mulig å gjøre endringer
                                på mer enn en faktura per ordre. Vennligst fullfør ordre for å legge til nye abonnement
                                på en ny faktura.
                            </Notification.Text>
                        </Notification>
                    )}
                </div>
            )}
            <ExistingSubscriptionDiscardModal
                open={!!discardModalSubscriptionId}
                onClose={() => setDiscardModalSubscriptionId(null)}
                onConfirm={async () => {
                    if (!discardModalSubscriptionId) {
                        return;
                    }

                    dispatch(removeItemByIdFromCart(discardModalSubscriptionId, false));
                    dispatch(setActiveSubscription());
                    setDiscardModalSubscriptionId(null);
                    if (existingSubscriptionForChange) {
                        dispatch(clearExistingSubscriptionForChange());
                        dispatch(setExistingSubscriptionForChange(undefined));
                    }
                }}
            />
        </div>
    );
};

export default AddNewSubscriptions;
